import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Button from "../../components/UI/Buttons/Button/Button";
import { getMyCustomers } from "../../services/volunteerProfileService";
import { UserResponse } from "../../types/shared";
import styles from "./MyCustomersPage.module.scss";

// ITAM-2734: adding another UserResponse field for the PM later is one entry
// in this array; label doubles as the cell's accessible name.
const CUSTOMER_COLUMNS: {
  key: string;
  label: string;
  render: (user: UserResponse) => React.ReactNode;
}[] = [
  {
    key: "photo",
    label: "Fotografija",
    render: (user) => (
      <img
        className={styles.customerPhoto}
        src={
          user.userPhoto
            ? `${process.env.REACT_APP_CDN_PATH}${user.userPhoto}`
            : "/profile.png"
        }
        alt=""
      />
    ),
  },
  {
    key: "name",
    label: "Ime i prezime",
    render: (user) => (
      <span className={styles.customerName}>
        {user.firstName} {user.secondName}
      </span>
    ),
  },
  {
    key: "phone",
    label: "Telefon",
    render: (user) =>
      user.phoneNumber ? (
        <a className={styles.customerPhone} href={`tel:${user.phoneNumber}`}>
          {user.phoneNumber}
        </a>
      ) : null,
  },
];

const MyCustomersPage = () => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  // Always the logged-in user's own id, never from the route.
  const userId = Number(userInfo?.id);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<UserResponse[]>([]);

  const loadCustomers = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError("Došlo je do greške. Pokušajte ponovo.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setCustomers(await getMyCustomers(userId));
    } catch (err) {
      setError("Greška pri učitavanju korisnika. Pokušajte ponovo.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Moji korisnici</h1>
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Korisnici koji su vas izabrali</h2>

        {loading && <p className={styles.hint}>Učitavanje...</p>}

        {!loading && error && (
          <>
            <p className={styles.error} role="alert">
              {error}
            </p>
            <Button buttonType="blue" onClick={loadCustomers}>
              Pokušaj ponovo
            </Button>
          </>
        )}

        {!loading && !error && customers.length === 0 && (
          <p className={styles.hint}>
            Nijedan korisnik vas još nije izabrao kao svoje ovlašćeno lice.
          </p>
        )}

        {!loading && !error && customers.length > 0 && (
          <ul className={styles.customerList}>
            {customers.map((customer) => (
              <li key={customer.id} className={styles.customerRow}>
                {CUSTOMER_COLUMNS.map((column) => (
                  <div
                    key={column.key}
                    className={styles.customerCell}
                    aria-label={column.label}
                  >
                    {column.render(customer)}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MyCustomersPage;
